/**
 * Custom Site Specific Javascript
 * Port of Seattle Data Breach Settlement — portdatasettlement.com
 */

import { modal } from "commonAssets/lib/modal";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { validateField } from "commonAssets/lib/validateField";

export function customInitClaim(claim) {
    console.log("[Initiate claim]...");

    _showDeficiencyReasons(claim.deficiency_reason);
    _initBenefitValidation();
    _initAlternateEmailToggle();
    _disableNonSignatureValidation();

    return claim;
}

export function customLoadMember(claim) {
    console.log("[Load class member]...");

    _prefillNameFields(claim.class_member);
    _lockNameFields();

    return claim;
}

export function customProcessClaim(claim) {
    console.log("[Process claim]...");

    // This deficiency portal accepts partial cures. The shared claim processor
    // runs its standard field, email-confirmation, upload, and payment checks
    // before this hook, so set the final validity state from the only required
    // fields: electronic signature and signature date.
    const signature = document.getElementById("signature");
    const signatureDate = document.getElementById("date");

    const isSignatureValid = signature && validateField(signature);
    const isSignatureDateValid = signatureDate && validateField(signatureDate);
    claim.flags.valid = Boolean(isSignatureValid && isSignatureDateValid);

    return claim;
}

/**
 * Payment selection is optional for deficiency cures. This bypasses the shared
 * Digital Disbursements requirement and its contact/payment email and phone
 * comparisons while preserving any payment information the claimant elects to provide.
 */
export function customDstProcess(claim) {
    return claim;
}

/**
 * Display the deficiency reasons returned for an eligible editable claim.
 */
function _showDeficiencyReasons(deficiencyReason) {
    if (!deficiencyReason) return;

    const escapeHtml = (value) =>
        String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\"/g, "&quot;")
            .replace(/'/g, "&#039;");
    const reasons = String(deficiencyReason)
        .split(",")
        .map((reason) => reason.trim())
        .filter(Boolean)
        .map((reason) => `<li>${escapeHtml(reason)}</li>`)
        .join("");

    if (!reasons) return;

    modal(
        "Claim Deficient",
        `<p>Please fix the issues below and resubmit your claim:</p><ul class="mb-0">${reasons}</ul>`
    );
}

/**
 * Remove all client-side constraints except the shared signature and date
 * fields. The shared processor evaluates every input with data-validate before
 * customProcessClaim runs, so these attributes must be removed as well as the
 * schema-level required flags.
 */
function _disableNonSignatureValidation() {
    document.querySelectorAll("#form [data-validate]").forEach((field) => {
        if (!["signature", "date"].includes(field.id)) {
            field.removeAttribute("data-validate");
            field.removeAttribute("required");
        }
    });
}

/**
 * Populate claimant names from the member lookup response before making them read-only.
 */
function _prefillNameFields(member = {}) {
    ["first_name", "last_name"].forEach((fieldName) => {
        const field = document.getElementById(fieldName);
        if (field && member[fieldName] != null) {
            field.value = member[fieldName];
        }
    });
}

/**
 * Disable name fields so pre-populated values cannot be edited.
 */
function _lockNameFields() {
    const nameFields = [
        "first_name",
        "last_name",
        "middle_initial",
        "suffix",
    ];

    nameFields.forEach((fieldName) => {
        const field = document.querySelector(`[name="${fieldName}"]`);
        if (field && field.value) {
            field.setAttribute("readonly", true);
            field.classList.add("bg-light");
        }
    });
}

/**
 * Set up benefit checkbox change listeners and optional-field visibility.
 */
function _initBenefitValidation() {
    const benefitA = document.querySelector('[name="benefit_a_oop_losses"]');
    const transactionsSection = document.querySelector("#transactions");
    const documentationSection = document.querySelector("#documentation");

    // By default, transactions and documentation sections are hidden.
    transactionsSection.classList.toggle("d-none", !benefitA.checked);
    documentationSection.classList.toggle("d-none", !benefitA.checked);

    // Show or hide Benefit A sub-fields based on the current checked state.
    const benefitAFields = document.querySelectorAll(
        ".toggle-benefit_a_oop_losses"
    );
    benefitAFields.forEach((element) => {
        element.parentElement.classList.toggle("d-none", !benefitA.checked);
    });

    if (benefitA) {
        benefitA.addEventListener("change", (event) => {
            transactionsSection.classList.toggle("d-none", !event.target.checked);
            documentationSection.classList.toggle(
                "d-none",
                !event.target.checked
            );
            benefitAFields.forEach((element) => {
                element.parentElement.classList.toggle(
                    "d-none",
                    !event.target.checked
                );
            });
        });
    }
}

/**
 * Show/hide the optional alternate email fields based on the checkbox.
 */
function _initAlternateEmailToggle() {
    const checkbox = document.querySelector('[name="has_alternate_email"]');
    const emailFields = document.querySelectorAll(".toggle-has_alternate_email");

    if (!checkbox) return;

    // Show or hide email field parent wrappers based on the current checked state.
    emailFields.forEach((element) => {
        element.parentElement.classList.toggle("d-none", !checkbox.checked);
    });

    // Show/hide on checkbox change; clear values and errors when hiding.
    checkbox.addEventListener("change", (event) => {
        const isChecked = event.target.checked;
        emailFields.forEach((element) => {
            element.parentElement.classList.toggle("d-none", !isChecked);
            if (!isChecked) {
                element.value = "";
                toggleFieldError(element, false);
            }
        });
    });
}
